import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Animated,
  Pressable,
  StyleSheet,
  TouchableOpacity,
  View,
  Text,
  useWindowDimensions,
  Platform
} from 'react-native';
import getGlobalStyles from '../globalStyles';
import { ModalType, useModalStore } from '../store/modalStore';
import LanguageModal from './LanguageModal';
import { SourcesModal } from './SourcesModal';
import AIAssistant from '../features/assistant/screens/AIAssistant';
import ShareScreen from '../features/share/screen/ShareScreen';
import ManageCollections from '../features/Collections/components/ManageCollections';
import { usePlatform } from '../providers/PlatformProvider';
import { useTranslation } from 'react-i18next';
import { useThemeSettings } from '../ThemeProvider';
import CustomLinearGradient, { GRADIENT_TYPE } from './CustomLinearGradient';
import AudioListModal from './AudioListModal';
import { FULL_WIDTH } from '../shared/constants/dimension';
import { MenuScreen } from '../routes/menu';
import ConfirmationModal from './ConfirmationModal';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
interface BottomModalProps {
  alignment?: "center" | "flex-start";
}
const BottomModal = ({ alignment = 'center' }: BottomModalProps) => {
  const { height: SCREEN_HEIGHT } = useWindowDimensions();
  const { t, i18n } = useTranslation();

  const { theme } = useThemeSettings();
  const translateY = useRef(new Animated.Value(SCREEN_HEIGHT)).current;
  const gStyles = useMemo(() => getGlobalStyles(theme), [theme]);
  const { modalShown, clearModal, modalAdditionalProps } = useModalStore();
  const { showWebVersion } = usePlatform();
  const insets = useSafeAreaInsets();
  const [title, setTitle] = useState('');
  useEffect(() => {
    //setDelayedTitle('');
    if (!showWebVersion) {
      Animated.timing(translateY, {
        toValue: modalShown !== ModalType.NONE ? 0 : SCREEN_HEIGHT,
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
    switch (modalShown) {
      case ModalType.MANAGE_COLLECTIONS:
        setDelayedTitle(t("screens.collections.title"));
        break;
      case ModalType.LANGUAGES:
        setDelayedTitle(t("screens.language.title"));
        break;
      case ModalType.SHARE_ARTICLE:
        setDelayedTitle(t("screens.share.title"));
        break;
      case ModalType.MENU:
        setDelayedTitle(t(" "));
        break;
      case ModalType.CONFIRM:
        setDelayedTitle(modalAdditionalProps?.title ?? t("screens.common.confirmation"))
        break;
      case ModalType.SOURCES:
        // setDelayedTitle(t("screens.sources.title", { count: 0 }));
        break;
      default:
        setDelayedTitle('');
        break;
    }
  }, [i18n.language, modalAdditionalProps?.title, modalShown]);

  const setDelayedTitle = (title: string) => {
    setTimeout(() => {
      setTitle(title)
    }, 100);
  }
  const hideTopClose = showWebVersion || [ModalType.AI_ASSISTANT,
  ModalType.SHARE_ARTICLE
  ].includes(modalShown);

  return (
    <>
      {modalShown !== ModalType.NONE && (
        <>
          <Pressable style={gStyles.backdrop} onPress={() => { modalAdditionalProps?.onReject?.(); clearModal(); }} />
          <Animated.View
            style={[styles.animatedContainer,

            gStyles.fullWidth,
            { maxHeight: SCREEN_HEIGHT - (Platform.OS !== 'web' ? 100 : 0) },
            !showWebVersion ? { transform: [{ translateY }], bottom: 0 } : { marginVertical: 10, flex: 1 }]
            }>
            {!hideTopClose && <TouchableOpacity style={[gStyles.buttonStyle, gStyles.buttonCircle, { height: 40, width: 40, backgroundColor: theme?.colors?.btnBgFill }]} onPress={() => { modalAdditionalProps?.onReject?.(); clearModal(); }}>
              <Text style={[styles.closeText, { color: theme?.colors?.primary }]}>
                ✕
              </Text>
            </TouchableOpacity>}

            <CustomLinearGradient gradientType={modalShown === ModalType.AI_ASSISTANT ? GRADIENT_TYPE.BASE_WEB : GRADIENT_TYPE.MODAL} style={[{ marginTop: 10, flex: 1, position: 'relative' }, gStyles.gradientBorderShadow]}>
              <View style={[styles.container,
              {
                alignItems: modalShown === ModalType.SOURCES ? 'flex-start' : alignment,
                padding: hideTopClose ? 0 : 12,
                paddingTop: 0,
                marginBottom: Platform.OS === 'web' ? 20 : insets.bottom
              }]}>
                <View style={[styles.headerRow, modalShown === ModalType.AI_ASSISTANT && { paddingVertical: 0 }]}>
                  <Text style={[gStyles.header1, { color: theme?.colors?.primary, width: FULL_WIDTH, textAlign: 'center' }]}>
                    {title || ''}
                  </Text>
                  {/* Close Button */}
                  {hideTopClose && modalShown !== ModalType.AI_ASSISTANT && <TouchableOpacity
                    onPress={() => { modalAdditionalProps?.onReject?.(); clearModal(); }}
                    style={styles.closeButton}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  >
                    <View style={[styles.closeCircle, { borderColor: theme?.colors?.primary }]}>
                      <Text style={[styles.closeText, { color: theme?.colors?.primary }]}>
                        ✕
                      </Text>
                    </View>
                  </TouchableOpacity>}
                </View>
                {modalShown === ModalType.LANGUAGES && <LanguageModal />}
                {modalShown === ModalType.SOURCES && <SourcesModal updateTitle={setDelayedTitle} />}
                {modalShown === ModalType.AI_ASSISTANT && <AIAssistant onClose={clearModal} />}
                {modalShown === ModalType.MANAGE_COLLECTIONS && <ManageCollections />}
                {modalShown === ModalType.SHARE_ARTICLE && <ShareScreen />}
                {modalShown === ModalType.MENU && <MenuScreen />}
                {modalShown === ModalType.AUDIO_LIST && <AudioListModal />}
                {modalShown === ModalType.CONFIRM && <ConfirmationModal onClose={clearModal} />}
              </View>
            </CustomLinearGradient>
            <View></View>
          </Animated.View>
        </>
      )}
    </>
  );
};

export default BottomModal;

const styles = StyleSheet.create({
  animatedContainer: {
    position: 'absolute',
    alignSelf: 'center',
    alignItems: 'center',
    zIndex: 1000,        // ✅ iOS / Web
    elevation: 20,      // ✅ Android
  },
  closeIconWrapper: {
    position: 'absolute',
    top: -100,
    justifyContent: 'center',
    alignSelf: 'center',
    alignItems: 'center'
  },
  container: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    flex: 1,
    padding: 12,
  },

  headerRow: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: 'row',
    alignContent: "center",
    padding: 10
  },

  closeButton: {
    marginRight: 0,
  },

  closeCircle: {
    width: 30,
    height: 30,
    borderRadius: 15,
    borderWidth: 2,
    justifyContent: "center",
    alignItems: "center",
  },

  closeText: {
    fontSize: 14,
    fontWeight: "900",
  },
});
